import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export interface AuthState {
  isAuthenticated: boolean
  token: string | null
  candidateId: string | null
  organizationId: string | null
  candidateName: string | null
  position: string | null
  employmentType: string | null
  duration: string | null
  psychometricResultUrl: string | null
  companyProfileUrl: string | null
  reportCallbackUrl: string | null
  sessionExpires: number | null
  // Noise consent data to log when interview starts
  noiseConsentData: {
    noiseLevel: 'low' | 'moderate' | 'high'
    recommendation: string
    consentGiven: boolean
    timestamp: string
  } | null
}

const initialState: AuthState = {
  isAuthenticated: false,
  token: null,
  candidateId: null,
  organizationId: null,
  candidateName: null,
  position: null,
  employmentType: null,
  duration: null,
  psychometricResultUrl: null,
  companyProfileUrl: null,
  reportCallbackUrl: null,
  sessionExpires: null,
  noiseConsentData: null,
}

export interface LoginPayload {
  token: string
  candidateId: string
  organizationId: string
  candidateName?: string
  position?: string
  employmentType?: string
  duration?: string
  psychometricResultUrl?: string
  companyProfileUrl?: string
  reportCallbackUrl?: string
  expiresIn?: number
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginSuccess: (state, action: PayloadAction<LoginPayload>) => {
      const { 
        token, candidateId, organizationId, candidateName, position, 
        employmentType, duration, psychometricResultUrl, companyProfileUrl, 
        reportCallbackUrl, expiresIn 
      } = action.payload
      
      state.isAuthenticated = true
      state.token = token
      state.candidateId = candidateId
      state.organizationId = organizationId
      state.candidateName = candidateName || null
      state.position = position || null
      state.employmentType = employmentType || null
      state.duration = duration || null
      state.psychometricResultUrl = psychometricResultUrl || null
      state.companyProfileUrl = companyProfileUrl || null
      state.reportCallbackUrl = reportCallbackUrl || null
      state.sessionExpires = expiresIn ? Date.now() + (expiresIn * 1000) : null
    },
    
    logout: (state) => {
      state.isAuthenticated = false
      state.token = null
      state.candidateId = null
      state.organizationId = null
      state.candidateName = null
      state.position = null
      state.sessionExpires = null
    },
    
    updateCandidateInfo: (state, action: PayloadAction<{ candidateName?: string; position?: string }>) => {
      const { candidateName, position } = action.payload
      if (candidateName) state.candidateName = candidateName
      if (position) state.position = position
    },
    
    checkTokenExpiration: (state) => {
      if (state.sessionExpires && Date.now() > state.sessionExpires) {
        // Token expired, clear auth state
        state.isAuthenticated = false
        state.token = null
        state.candidateId = null
        state.organizationId = null
        state.candidateName = null
        state.position = null
        state.sessionExpires = null
        state.noiseConsentData = null
      }
    },

    storeNoiseConsent: (state, action: PayloadAction<{
      noiseLevel: 'low' | 'moderate' | 'high'
      recommendation: string
      consentGiven: boolean
    }>) => {
      state.noiseConsentData = {
        ...action.payload,
        timestamp: new Date().toISOString()
      }
    },

    clearNoiseConsent: (state) => {
      state.noiseConsentData = null
    },
  },
})

export const { loginSuccess, logout, updateCandidateInfo, checkTokenExpiration, storeNoiseConsent, clearNoiseConsent } = authSlice.actions

export default authSlice.reducer

// Selectors
export const selectAuth = (state: { auth: AuthState }) => state.auth
export const selectIsAuthenticated = (state: { auth: AuthState }) => state.auth.isAuthenticated
export const selectToken = (state: { auth: AuthState }) => state.auth.token
export const selectCandidateInfo = (state: { auth: AuthState }) => ({
  candidateId: state.auth.candidateId,
  organizationId: state.auth.organizationId,
  candidateName: state.auth.candidateName,
  position: state.auth.position,
})